<div class="calculator-container">
  <div class="calculator-header">
    <h1>Relationship Calculator</h1>
    <p class="subtitle">Select two people to discover their exact relationship</p>
  </div>

  <div class="calculator-content">
    <!-- Person Selection Section -->
    <div class="selection-section">
      <!-- Person 1 -->
      <div class="person-selector">
        <label class="selector-label">Person 1</label>
        <div class="searchable-dropdown">
          <div class="input-wrapper">
            <input
              type="text"
              class="search-input"
              [class.disabled]="isPerson1Disabled"
              [(ngModel)]="person1SearchTerm"
              (input)="searchPerson1()"
              (focus)="showPerson1Dropdown = person1SearchTerm.length >= 2 && !isPerson1Disabled"
              placeholder="Search and select first person..."
              [disabled]="isLoadingRelationship || isPerson1Disabled">
            <button
              *ngIf="selectedPerson1 && !isPerson1Disabled"
              type="button"
              class="clear-btn"
              (click)="clearPerson1()"
              title="Clear selection">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
          <div class="dropdown-results" *ngIf="showPerson1Dropdown && person1Results.length > 0">
            <div
              class="dropdown-item"
              *ngFor="let relative of person1Results"
              (click)="selectPerson1(relative)">
              <div class="item-name">{{ relative.firstName }} {{ relative.middleName || '' }} {{ relative.lastName }}</div>
              <div class="item-details" *ngIf="relative.email || relative.mobileNumber">
                <span *ngIf="relative.email">{{ relative.email }}</span>
                <span *ngIf="relative.mobileNumber"> • {{ relative.mobileNumber }}</span>
              </div>
            </div>
          </div>
          <div class="dropdown-results" *ngIf="showPerson1Dropdown && isLoadingPerson1">
            <div class="dropdown-item loading">Searching...</div>
          </div>
          <div class="dropdown-results" *ngIf="showPerson1Dropdown && !isLoadingPerson1 && person1SearchTerm.length >= 2 && person1Results.length === 0">
            <div class="dropdown-item no-results">No results found</div>
          </div>
        </div>
      </div>

      <!-- VS Divider -->
      <div class="vs-divider">
        <div class="vs-line"></div>
        <div class="vs-text">VS</div>
        <div class="vs-line"></div>
      </div>

      <!-- Person 2 -->
      <div class="person-selector">
        <label class="selector-label">Person 2</label>
        <div class="searchable-dropdown">
          <div class="input-wrapper">
            <input
              type="text"
              class="search-input"
              [(ngModel)]="person2SearchTerm"
              (input)="searchPerson2()"
              (focus)="showPerson2Dropdown = person2SearchTerm.length >= 2"
              placeholder="Search and select second person..."
              [disabled]="isLoadingRelationship">
            <button
              *ngIf="selectedPerson2"
              type="button"
              class="clear-btn"
              (click)="clearPerson2()"
              title="Clear selection">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
          <div class="dropdown-results" *ngIf="showPerson2Dropdown && person2Results.length > 0">
            <div
              class="dropdown-item"
              *ngFor="let relative of person2Results"
              (click)="selectPerson2(relative)">
              <div class="item-name">{{ relative.firstName }} {{ relative.middleName || '' }} {{ relative.lastName }}</div>
              <div class="item-details" *ngIf="relative.email || relative.mobileNumber">
                <span *ngIf="relative.email">{{ relative.email }}</span>
                <span *ngIf="relative.mobileNumber"> • {{ relative.mobileNumber }}</span>
              </div>
            </div>
          </div>
          <div class="dropdown-results" *ngIf="showPerson2Dropdown && isLoadingPerson2">
            <div class="dropdown-item loading">Searching...</div>
          </div>
          <div class="dropdown-results" *ngIf="showPerson2Dropdown && !isLoadingPerson2 && person2SearchTerm.length >= 2 && person2Results.length === 0">
            <div class="dropdown-item no-results">No results found</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Relationship Result Section -->
    <div class="result-section" *ngIf="relationshipResult || isLoadingRelationship || errorMessage">
      <div class="result-card" *ngIf="isLoadingRelationship">
        <div class="progress-container">
          <div class="progress-header">
            <h3>Calculating Relationship</h3>
            <span class="elapsed-time">{{ elapsedTime }}s</span>
          </div>
          
          <div class="progress-bar-wrapper">
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="progressPercentage"></div>
            </div>
            <div class="progress-percentage">{{ Math.round(progressPercentage) }}%</div>
          </div>
          
          <div class="progress-info">
            <div class="progress-message">
              <span class="progress-icon">🔍</span>
              <span>{{ progressMessage }}</span>
            </div>
            <div class="progress-phase" [class.phase-direct]="progressPhase === 'Direct'"
                 [class.phase-ancestor]="progressPhase === 'CommonAncestor'"
                 [class.phase-inlaw]="progressPhase === 'InLaw'"
                 [class.phase-bfs]="progressPhase === 'BFS'">
              {{ getPhaseLabel(progressPhase) }}
            </div>
          </div>
          
          <div class="progress-steps">
            <div class="progress-step" [class.active]="progressPhase === 'Direct'" [class.completed]="getPhaseIndex(progressPhase) > 0">
              <div class="step-dot"></div>
              <span>Direct Check</span>
            </div>
            <div class="progress-step" [class.active]="progressPhase === 'CommonAncestor'" [class.completed]="getPhaseIndex(progressPhase) > 1">
              <div class="step-dot"></div>
              <span>Common Ancestor</span>
            </div>
            <div class="progress-step" [class.active]="progressPhase === 'InLaw'" [class.completed]="getPhaseIndex(progressPhase) > 2">
              <div class="step-dot"></div>
              <span>In-Law Check</span>
            </div>
            <div class="progress-step" [class.active]="progressPhase === 'BFS'" [class.completed]="getPhaseIndex(progressPhase) > 3">
              <div class="step-dot"></div>
              <span>BFS Search</span>
            </div>
          </div>
        </div>
      </div>

      <div class="result-card error" *ngIf="errorMessage && !isLoadingRelationship">
        <div class="error-icon">⚠️</div>
        <p>{{ errorMessage }}</p>
      </div>

      <div class="result-card success" *ngIf="relationshipResult && !isLoadingRelationship">
        <div class="result-header">
          <h2>Relationship Result</h2>
        </div>
        <div class="result-content">
          <div class="relationship-display">
            <div class="person-display clickable" (click)="goToHierarchy(relationshipResult.person1Id)">
              <div class="person-avatar">
                <img *ngIf="getPerson1ImageUrl()" [src]="getPerson1ImageUrl()" alt="{{ relationshipResult.person1Name }}" class="avatar-img" />
                <div class="avatar-circle" *ngIf="!getPerson1ImageUrl()">
                  {{ getInitials(relationshipResult.person1Name) }}
                </div>
              </div>
              <div class="person-name">{{ relationshipResult.person1Name }}</div>
            </div>
            <div class="relationship-arrow">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="relationship-type">
              <span class="relationship-label">{{ relationshipResult.relationship }}</span>
              <span class="relationship-detail" *ngIf="relationshipResult.detailedRelationship">
                {{ relationshipResult.detailedRelationship }}
              </span>
            </div>
            <div class="relationship-arrow">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 5M5 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="person-display clickable" (click)="goToHierarchy(relationshipResult.person2Id)">
              <div class="person-avatar">
                <img *ngIf="getPerson2ImageUrl()" [src]="getPerson2ImageUrl()" alt="{{ relationshipResult.person2Name }}" class="avatar-img" />
                <div class="avatar-circle" *ngIf="!getPerson2ImageUrl()">
                  {{ getInitials(relationshipResult.person2Name) }}
                </div>
              </div>
              <div class="person-name">{{ relationshipResult.person2Name }}</div>
            </div>
          </div>
          
          <!-- Path Method Badge -->
          <div class="path-method-badge" *ngIf="relationshipResult.pathMethod">
            <span class="method-label">Method:</span>
            <span class="method-value">{{ getPathMethodLabel(relationshipResult.pathMethod) }}</span>
          </div>

          <!-- Enhanced Detailed Path -->
          <div class="relationship-path" *ngIf="relationshipResult.detailedPath && relationshipResult.detailedPath.length > 0">
            <div class="path-label">Detailed Relationship Path:</div>
            <div class="path-container">
              <div class="path-visual">
                <ng-container *ngFor="let segment of relationshipResult.detailedPath; let i = index; let isLast = last">
                  <!-- Show person node -->
                  <div class="path-segment path-person clickable" (click)="goToHierarchy(segment.relativeId)">
                    <div class="path-node">
                      <div class="node-avatar-container">
                        <img *ngIf="segment.profileImageUrl" 
                             [src]="segment.profileImageUrl" 
                             [alt]="segment.relativeName" 
                             class="node-avatar-img" />
                        <div class="node-circle" 
                             *ngIf="!segment.profileImageUrl"
                             [style.background-color]="getRelationshipTypeColor(segment.relationshipType)">
                          <span class="node-icon">{{ getRelationshipTypeIcon(segment.relationshipType) }}</span>
                        </div>
                        <div class="node-circle-overlay" 
                             *ngIf="segment.profileImageUrl"
                             [style.border-color]="getRelationshipTypeColor(segment.relationshipType)">
                        </div>
                      </div>
                      <div class="node-info">
                        <div class="node-name">{{ segment.relativeName }}</div>
                        <div class="node-relationship" [style.color]="getRelationshipTypeColor(segment.relationshipType)">
                          {{ segment.relationshipType }}
                        </div>
                        <div class="node-distance" *ngIf="segment.distance > 0">
                          {{ segment.distance }} step{{ segment.distance !== 1 ? 's' : '' }} away
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Show connector with relationship label -->
                  <div class="path-connector" *ngIf="!isLast">
                    <div class="connector-line" [style.background-color]="getRelationshipTypeColor(relationshipResult.detailedPath![i + 1].relationshipType)"></div>
                    <div class="connector-label" [style.color]="getRelationshipTypeColor(relationshipResult.detailedPath![i + 1].relationshipType)">
                      {{ relationshipResult.detailedPath![i + 1].relationshipType }}
                    </div>
                  </div>
                </ng-container>
              </div>
            </div>
          </div>

          <!-- Fallback to legacy path if detailedPath is not available -->
          <div class="relationship-path" *ngIf="(!relationshipResult.detailedPath || relationshipResult.detailedPath.length === 0) && relationshipResult.relationshipPath && relationshipResult.relationshipPath.length > 0">
            <div class="path-label">Detailed Relationship Path:</div>
            <div class="path-container">
              <div class="path-visual">
                <ng-container *ngFor="let item of relationshipResult.relationshipPath; let i = index; let isLast = last">
                  <!-- Show person node -->
                  <div class="path-segment path-person" *ngIf="item !== 'Child of' && item !== 'Parent of' && item !== 'Spouse'">
                    <div class="path-node">
                      <div class="node-circle">
                        <span class="node-initial">{{ item.charAt(0) }}</span>
                      </div>
                      <div class="node-name">{{ item }}</div>
                    </div>
                  </div>
                  <!-- Show connector with relationship label -->
                  <div class="path-connector" *ngIf="!isLast && (item === 'Child of' || item === 'Parent of' || item === 'Spouse')">
                    <div class="connector-line"></div>
                    <div class="connector-label">{{ item }}</div>
                  </div>
                </ng-container>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

